<script setup>
import { ref, onMounted, defineEmits, computed, watch } from 'vue';
import { convertToBase64File, formatearInputMoneda } from '@/utils/helpers';
import { descargarDatosBancarios } from '@/utils/components';
import Modal from 'bootstrap/js/dist/modal';

import logoGalicia from '@/assets/galicia.png';
import logoBrubank from '@/assets/brubank.png';
import logoMP from '@/assets/mercadopago.png';

const emit = defineEmits(['closeCargarGarantias']);

const isSaving = ref(false);

const props = defineProps({
  garantiasStore: { type: Object, required: true },
});

const garantiaStores = props.garantiasStore;

const hoy = ref(new Date().toISOString().split('T')[0]);
const archivoNombre = ref('');
const archivoPreview = ref('');
const arrastrando = ref(false);

const destinoSeleccionado = ref(null);

const logosPorSlugBase = {
  galicia: logoGalicia,
  brubank: logoBrubank,
  mercadopago: logoMP,
};

function normalizarSlugBase(slug) {
  return (slug || '')
    .toLowerCase()
    .replace(/-ars$/, '')
    .replace(/mercado-pago/, 'mercadopago');
}

function logoForSlug(slug) {
  const base = normalizarSlugBase(slug);
  return logosPorSlugBase[base] || null;
}

function seleccionarDestino(destino) {
  destinoSeleccionado.value = destino;
  garantiaStores.nueva_garantia.medio_pago_destino_id = destino.id;
}

watch(
  () => garantiaStores.nueva_garantia.medio_pago_id,
  () => {
    destinoSeleccionado.value = null;
    garantiaStores.nueva_garantia.banco_billetera = '';
    garantiaStores.nueva_garantia.cbu = '';
  }
);

onMounted(() => {
  garantiaStores.nueva_garantia.fecha_operacion = hoy.value;

  const modal = document.getElementById('carga-garantia-modal');
  if (modal) {
    modal.addEventListener('shown.bs.modal', () => {
      modal.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
});

const existenErrores = computed(
  () => Array.isArray(garantiaStores.errores) && garantiaStores.errores.length > 0
);

const medioPagoSeleccionado = computed(() => {
  return garantiaStores.medios_de_pago.find(
    (m) => m.id === garantiaStores.nueva_garantia.medio_pago_id
  );
});

const mostrarCBU = computed(() => {
  const slug = medioPagoSeleccionado.value?.slug;
  return slug === 'pago-billetera-virtual' || slug === 'transferencia';
});

const formatearNumero = (campo, event) => {
  garantiaStores.nueva_garantia[campo] = event.target.value.replace(/\D/g, '');
};

function esImagen(mime) {
  return ['image/jpeg', 'image/png', 'image/webp'].includes(mime);
}

async function procesarArchivo(file) {
  const maxBytes = 5 * 1024 * 1024;
  const tiposPermitidos = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

  garantiaStores.errores = [];

  if (!tiposPermitidos.includes(file.type)) {
    garantiaStores.errores.push('Formato no permitido. Usá JPG, PNG, WEBP o PDF.');
    archivoNombre.value = '';
    archivoPreview.value = '';
    return;
  }

  if (file.size > maxBytes) {
    garantiaStores.errores.push('El archivo supera los 5MB.');
    archivoNombre.value = '';
    archivoPreview.value = '';
    return;
  }

  const extension = (file.name.split('.').pop() || '').toLowerCase();
  const fileBase64 = await convertToBase64File(file);
  const fileData = { file: fileBase64, extension };

  garantiaStores.agregarComprobante(fileData);
  archivoNombre.value = file.name;
  archivoPreview.value = esImagen(file.type) ? fileBase64 : '';
}

async function onFileChange(e) {
  const file = e.target.files?.[0];
  if (file) await procesarArchivo(file);
}

function onDragOver(e) {
  e.preventDefault();
  arrastrando.value = true;
}
function onDragLeave() {
  arrastrando.value = false;
}
async function onDrop(e) {
  e.preventDefault();
  arrastrando.value = false;
  const file = e.dataTransfer.files?.[0];
  if (file) await procesarArchivo(file);
}

const seleccionarMedio = (medioId) => {
  garantiaStores.nueva_garantia.medio_pago_id = medioId;
};

const enviarDatosGarantia = async () => {
  garantiaStores.errores = [];
  if (isSaving.value) return;

  isSaving.value = true;

  if (mostrarCBU.value) {
    const cbu = (garantiaStores.nueva_garantia.cbu || '').trim();
    if (!cbu) {
      garantiaStores.errores.push('El campo CBU es obligatorio para el medio de pago seleccionado.');
    } else if (!/^\d{22}$/.test(cbu)) {
      garantiaStores.errores.push('El CBU debe tener 22 dígitos.');
    }
  }
  if (garantiaStores.errores.length > 0) {
    isSaving.value = false;
    return;
  }

  archivoNombre.value = '';
  archivoPreview.value = '';
  await garantiaStores.enviarGarantia();

  if (!existenErrores.value) {
    isSaving.value = false;
    const modalEl = document.getElementById('carga-garantia-modal');
    const modalInstance = Modal.getOrCreateInstance(modalEl);
    modalInstance.hide();
    emit('closeCargarGarantias');
  }

  isSaving.value = false;
};
</script>

<template>
  <div class="modal fade" id="carga-garantia-modal" tabindex="-1" aria-labelledby="carga-garantia-modal-label"
    aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-xl" role="document">
      <div class="modal-content p-4">
        <div class="d-flex justify-content-between align-items-start px-4 pt-4">
          <div class="d-flex align-items-center">
            <i class="fa fa-shield-check fa-lg text-primary"></i>
            <div>
              <h6 id="carga-garantia-modal-label" class="mb-2 fw-400 fs-2">Informar Garantía</h6>
              <p class="mb-0 fs-14">
                Completá los campos solicitados una vez realizada la operación y adjuntá el comprobante.
                El pago debe realizarse mediante <span class="fw-600">transferencia bancaria, eCheq o depósito
                  bancario</span>.
              </p>
              <button class="mt-3 btn btn-soft-green text-white" @click="descargarDatosBancarios()">
                <i class="fa-solid fa-download fs-6 me-1"></i>
                Descargar datos bancarios
              </button>
            </div>
          </div>

          <button type="button"
            class="bg-white border-0 rounded-circle d-flex align-items-center justify-content-center"
            style="width: 40px; height: 40px;" data-bs-dismiss="modal" aria-label="Cerrar">
            <span class="fs-2 text-muted">&times;</span>
          </button>
        </div>

        <div class="modal-body p-4">
          <transition name="fade">
            <div v-if="existenErrores" class="alert bg-soft-red text-white" role="alert">
              <ul class="mb-0 ps-1">
                <li v-for="(error, i) in garantiaStores.errores" :key="i">{{ error }}</li>
              </ul>
            </div>
          </transition>

          <div class="row g-4">
            <div class="col-12 col-lg-7">
              <div class="card border-0 rounded-4 shadow-none">
                <div class="card-body p-0">
                  <div class="mb-4">
                    <label for="importe" class="fw-bold text-black m-0 mb-2 fs-13">Importe<span>*</span></label>
                    <div class="custom-input-icon">
                      <input id="importe" type="text" class="form-control input-with-icon" placeholder="$"
                        :value="formatearInputMoneda(garantiaStores.nueva_garantia?.monto)"
                        @input="formatearNumero('monto', $event)" inputmode="numeric" pattern="\d*" maxlength="12"
                        aria-describedby="importeHelp" />
                    </div>
                    <div id="importeHelp" class="form-text mt-2">
                      Solo números (sin puntos ni símbolos)
                    </div>
                  </div>

                  <div class="mb-4">
                    <div class="d-flex align-items-center justify-content-between">
                      <label class="fw-bold text-black m-0 mb-2 fs-13">Medio de pago</label>
                    </div>
                    <div class="d-flex flex-wrap gap-2">
                      <button v-for="medio in garantiaStores?.medios_de_pago" :key="medio.id" type="button"
                        class="btn btn-outline-success"
                        :class="{ 'btn-chip-active': garantiaStores.nueva_garantia.medio_pago_id === medio.id }"
                        @click="seleccionarMedio(medio.id)">
                        <i class="fa fa-wallet me-2"></i>{{ medio.nombre }}
                      </button>
                    </div>
                  </div>

                  <div class="mb-3">
                    <div class="d-flex flex-wrap gap-2">
                      <button
                        v-for="d in garantiasStore.medios_pago_destino"
                        :key="d.id"
                        type="button"
                        class="btn btn-outline-secondary bank-chip"
                        :class="{ 'bank-chip-active': destinoSeleccionado?.id === d.id }"
                        @click="seleccionarDestino(d)"
                      >
                        <img
                          v-if="logoForSlug(d.slug)"
                          :src="logoForSlug(d.slug)"
                          :alt="d.entidad_bancaria"
                          class="bank-logo"
                        />
                        <span v-else>{{ d.entidad_bancaria }}</span>
                      </button>
                    </div>

                    <div v-if="destinoSeleccionado" class="mt-2 small text-muted">
                      <div><strong>Titular:</strong> {{ destinoSeleccionado.titular_cuenta }}</div>
                      <div><strong>Alias:</strong> {{ destinoSeleccionado.alias }}</div>
                      <div v-if="destinoSeleccionado.numero_cuenta"><strong>N° Cuenta:</strong> {{ destinoSeleccionado.numero_cuenta }}</div>
                      <div><strong>CUIT:</strong> {{ destinoSeleccionado.cuit }}</div>
                    </div>
                  </div>

                  <div class="row g-3">
                    <div class="col-12 col-md-6">
                      <label for="fechaPago" class="fw-bold text-black m-0 mb-2 fs-13">Fecha de pago*</label>
                      <div class="custom-input-icon">
                        <input id="fechaPago" class="form-control input-with-icon-fecha" type="date"
                          :value="garantiaStores.nueva_garantia.fecha_operacion" :max="hoy" />
                      </div>
                    </div>

                    <div class="col-12 col-md-6">
                      <label for="moneda" class="fw-bold text-black m-0 mb-2 fs-13">Moneda*</label>
                      <div class="custom-input-icon">
                        <select id="moneda" class="input-with-arrow w-100 disabled"
                          v-model="garantiaStores.nueva_garantia.moneda" disabled>
                          <option value="ARS">ARS</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div class="row g-3 mt-1">
                    <div class="col-12">
                      <label for="dni" class="fw-bold text-black m-0 mb-2 fs-13">DNI*</label>
                      <div class="custom-input-icon">
                        <input id="dni" type="text" class="form-control input-with-icon"
                          v-model="garantiaStores.nueva_garantia.documento" inputmode="numeric"
                          placeholder="DNI del titular de la cuenta" pattern="\d*" maxlength="11" />
                      </div>
                    </div>

                    <div class="col-12 col-md-6">
                      <label for="nroTx" class="fw-bold text-black m-0 mb-2 fs-13">Número de transacción*</label>
                      <div class="custom-input-icon">
                        <input id="nroTx" type="text" class="form-control input-with-icon"
                          v-model="garantiaStores.nueva_garantia.numero_referencia" />
                      </div>
                    </div>

                    <div class="col-12 col-md-6">
                      <label for="banco_billetera" class="fw-bold text-black m-0 mb-2 fs-13">Banco / Billetera virtual</label>
                      <div class="custom-input-icon">
                        <input id="banco_billetera" type="text" class="form-control input-with-icon"
                          v-model="garantiaStores.nueva_garantia.banco_billetera" />
                      </div>
                    </div>

                    <div class="col-12" v-if="mostrarCBU">
                      <label for="cbu" class="fw-bold text-black m-0 mb-2 fs-13">CBU (22 dígitos)*</label>
                      <div class="custom-input-icon">
                        <input id="cbu" type="text" class="form-control input-with-icon"
                          v-model="garantiaStores.nueva_garantia.cbu" inputmode="numeric" placeholder="22 dígitos"
                          pattern="\d*" maxlength="22" />
                      </div>
                      <div class="form-text mt-1">Ingresá 22 dígitos sin espacios ni guiones.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-5">
              <div class="dropzone rounded-4 shadow-sm" :class="{ 'dropzone--active': arrastrando }"
                @dragover="onDragOver" @dragleave="onDragLeave" @drop="onDrop">
                <div class="text-center p-4">
                  <div class="icon-wrap mb-4">
                    <img src="@/assets/img/icons/cloud.svg" alt="Nube">
                  </div>

                  <h6 class="mb-1 text-soft-grey-74 fs-16">
                    {{ archivoPreview ? 'Archivo cargado' : 'Arrastra tu comprobante acá' }}
                  </h6>
                  <p class="text-muted small mb-3">
                    <span class="text-soft-grey-74 text-decoration-underline fs-13">O hacé click para seleccionar un archivo</span>
                  </p>

                  <input id="comprobanteCargaInput" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" class="d-none"
                    @change="onFileChange" />
                  <label for="comprobanteCargaInput" class="btn btn-outline-success w-100 ms-0">
                    <i class="fa fa-folder-open me-2"></i> Elegir archivo
                  </label>

                  <div class="mt-3">
                    <p class="text-soft-grey-74 fs-12 text-center">Permitidos: JPG, PNG, WEBP, PDF. Máx 5MB.</p>
                  </div>

                  <!-- Preview -->
                  <transition name="fade">
                    <div v-if="archivoNombre" class="mt-4">
                      <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                        <span class="badge bg-light text-dark border">
                          <i class="fa fa-file me-2"></i>{{ archivoNombre }}
                        </span>
                      </div>

                      <div v-if="archivoPreview" class="preview-wrap mt-3">
                        <img :src="archivoPreview" alt="Comprobante" class="img-fluid rounded-3 shadow-sm" />
                      </div>
                      <div v-else class="mt-2 text-muted small">
                        (PDF seleccionado — sin vista previa)
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer sticky-footer">
          <button class="btn btn-outline-success px-5" data-bs-dismiss="modal">
            Cancelar
          </button>
          <button class="btn btn-soft-green px-5" @click="enviarDatosGarantia">
            Enviar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.input-with-arrow:disabled {
  cursor: not-allowed !important;
}

.btn-chip-active {
  background-color: #006C4C !important;
  color: #fff !important;
  border-color: #006C4C !important;
}

.dropzone {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px dashed #006C4C;
  min-height: 280px;
  display: grid;
  place-items: center;
  transition: all .18s ease-in-out;
}

.dropzone--active {
  border-color: #0d6efd;
  background: #eef5ff;
}

.icon-wrap i {
  font-size: 2.25rem;
  color: #0d6efd;
}

.preview-wrap img {
  max-height: 220px;
  object-fit: contain;
  background: #fff;
}

.sticky-footer {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, .9);
  backdrop-filter: blur(6px);
  border-top: 1px solid rgba(0, 0, 0, .06);
  box-shadow: 0 -6px 16px rgba(0, 0, 0, .05);
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .18s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.modal-dialog {
  max-width: 1066px !important;
  width: 100%;
}


.bank-logo { height: 22px; display:block; }
.bank-chip { padding:.4rem .6rem; background:#fff; }
.bank-chip-active { border-color:#006C4C; box-shadow:0 0 0 .2rem rgba(0,108,76,.15); }
</style>
